/* eslint-disable max-lines-per-function */
import { Link, Router } from "@reach/router";
import React from "react";
import { Grid } from "@material-ui/core";
import { useQuery } from "@apollo/client";
import { Viewer } from "components/auth/types";
import { LoadingSpinner, ServerError } from "components/graphql-views";
import NotFound from "pages/not-found";
import { ProcessRemovalsView,
  ProcessRemovalsViewVariables } from "types/gql-types";
import { ContainerCardLayout } from "components/haven-design/card/index";
import { Channel } from "../../types";
import { PROCESS_REMOVALS_VIEW } from "../query";
import { Template } from "../types";
import styles from "./index.module.css";
import StepTemplate from "./step-choose-template";
import StepRemovals from "./step-removals";
import StepReview from "./step-review-send";
import StepSuccess from "./step-success";

interface Props {
  list?: string;
  path: string;
  viewer: Viewer;
  "*"?: string;
}

const activeLinkStyle = {
  textDecoration: "underline",
  fontWeight: 800,
};

const ProcessUpdateDenials: React.FC<Props> = (props) => {
  const [emailTemplate, setEmailTemplate] = React.useState<Template | null>(null);
  const [smsTemplate, setSmsTemplate] = React.useState<Template | null>(null);
  const [letterTemplate, setLetterTemplate] = React.useState<Template | null>(null);
  const [hasSkippedLetters, setSkipLetters] = React.useState(false);
  const currentPageName = props["*"];

  const results =
  useQuery<ProcessRemovalsView, ProcessRemovalsViewVariables>(PROCESS_REMOVALS_VIEW, {
    variables: { list: props.list ?? "",
      limit: 50,
      offset: 0 },
    fetchPolicy: "cache-first",
  });

  const { loading, error, data } = results;

  if (error) return <ServerError desc={error.message} />;
  if (loading) return <LoadingSpinner />;
  if (!data) return <ServerError desc="Missing Data" />;

  const letterStepComplete = hasSkippedLetters ? true : !!letterTemplate;
  const allStepsComplete = !!(emailTemplate && smsTemplate && letterStepComplete);
  const removedRequests = data.interestList.scheduledUpdate.removals.results;
  const title = () => (<><Link to={`/dash/manager/update/${props.list}`}>
    {data.interestList.name} Waitlist Update
  </Link>{" "}&ndash; Process Removals</>);
  return (
    <ContainerCardLayout title={title()}>
      <Grid container spacing={2}>
        <Grid item md={2}>
          <Link
            to="removals"
            className={styles.sideBarItem}
            style={currentPageName === "removals" ? activeLinkStyle : {}}
          >
        Review Applicants
          </Link>

          <Link
            to="review"
            className={`${styles.sideBarItem}`}
            style={currentPageName === "review" ? activeLinkStyle : {}}
          >
        Review & Send
          </Link>
        </Grid>

        <Grid item md={10}>
          <Router primary={false}>
            <StepRemovals
              path="removals/*"
              results={results}
            />

            <StepTemplate
              path="choose-email/*"
              requests={removedRequests}
              templates={data.templates}
              setTemplate={setEmailTemplate}
              currentTemplate={emailTemplate}
              channel={Channel.EMAIL}
              isStepComplete={!!emailTemplate}
            />

            <StepTemplate
              path="choose-sms/*"
              requests={removedRequests}
              templates={data.templates}
              setTemplate={setSmsTemplate}
              currentTemplate={smsTemplate}
              channel={Channel.SMS}
              isStepComplete={!!smsTemplate}
            />

            <StepTemplate
              path="choose-letter/*"
              requests={removedRequests}
              templates={data.templates ?? []}
              setTemplate={setLetterTemplate}
              currentTemplate={letterTemplate}
              channel={Channel.LETTER}
              isStepComplete={letterStepComplete}
              hasSkippedStep={hasSkippedLetters}
              setSkipStep={setSkipLetters}
            />

            <StepReview
              path="review/*"
              results={results}
              letterTemplate={letterTemplate}
              emailTemplate={emailTemplate}
              smsTemplate={smsTemplate}
              stepComplete={allStepsComplete}
              hasSkippedLetters={hasSkippedLetters}
              viewer={props.viewer}
            />

            <StepSuccess path="success/*" />

            <NotFound default />
          </Router>
        </Grid>
      </Grid>
    </ContainerCardLayout>
  );
};

export default ProcessUpdateDenials;
